import React, {
  useCallback, useEffect, useState, useMemo, useContext,
} from 'react';
import { Grid } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import EditorJS from '@editorjs/editorjs';
import { useFormContext } from 'react-hook-form';
import uuid from 'uuid';

import { useFormikContext } from 'formik';
import { DocumentPageInstruction, InstructionTypes } from '../../../../types/instructions';
import {
  setOpenDocumentPageInstructionModal,
  getDocumentListRequest,
  getArticleDocumentPagesRequest,
} from '../../../../store/modules/script-editor/actions';
import { selectDocumentPageInstruction } from '../../../../store/modules/script-editor/selectors';
import { selectProject } from '../../../../store/modules/projects/selectors';
import {
  InstructionEvent,
  InstructionActionTooltip,
} from '../types';
import { getQueryFilter } from '../../../../utility/helpers';
import { ListParams } from '../../../../types/main';
import {
  getLargeDocumentThumbnailUrl,
  getSmallDocumentThumbnailUrl,
} from '../../../../views/projects/tab-media/media/utils';
import { parseJsonToEditorStateWithInstruction } from '../../../../services/editorJS/utils';
import {
  emitInstructionEventByEditor,
  filterProcessedFiles,
  removeInstructionElement,
  setInstructionActionTooltip,
  sortDocumentsByTitle,
} from '../utils';
import {
  ModalComponent,
  InputComponent,
  SelectComponent,
  ProcessingMedia,
  SmallImageItem,
} from '../components';
import useStyles from './styles';
import { ScreenLocations } from '../../../../configs/instructionConfig';
import {
  setMediaList,
  setOpenMediaViewer,
  setCurrentDocumentPageMediaViewer,
} from '../../../../store/modules/media-viewer/actions';
import { DeferredActionContext } from '../../../../services/common/DeferredActionContext';
import DuplicateBlock from '../../../../services/editorJS/DuplicateBlock';
import { notify } from '../../../../store/modules/notifications/actions';

import { FormSimpleAutoComplete } from '../../../index';
import { Option } from '../../../form-simple-autocomplete/FormSimpleAutoComplete';
import MediaFileMissingMessage from '../components/MediaFileMissingMessage';

type Props = {
  instanceEditor: EditorJS | undefined;
};

const scriptFieldName = 'script';

const defaultState = {
  comment: '',
  screenLocation: 0,
  page: 1,
  documentId: -1,
};

const DocumentInstructionModal = ({ instanceEditor }: Props) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { getValues, setValue } = useFormContext();
  const { addAction, clearActions } = useContext(DeferredActionContext);
  const currentProject = useSelector(selectProject);
  const {
    isOpenModal, instructionId, documents, documentPages, isInline,
  } = useSelector(selectDocumentPageInstruction);
  const { setFieldValue } = useFormikContext();

  const [comment, setComment] = useState(defaultState.comment);
  const [page, setPage] = useState(defaultState.page);
  const [screenLocation, setScreenLocation] = useState(defaultState.screenLocation);
  const [documentId, setDocumentId] = useState(defaultState.documentId);
  const [initialState, setInitialState] = useState(defaultState);

  useEffect(() => () => {
    clearActions();
  }, [clearActions]);

  useEffect(() => {
    if (isOpenModal) {
      setFieldValue('document' as never, documentId);
    }
  }, [isOpenModal, documentId, setFieldValue]);

  useEffect(() => {
    if (isOpenModal) {
      setFieldValue('page' as never, page);
    }
  }, [isOpenModal, page, setFieldValue]);

  useEffect(() => {
    if (instructionId) {
      (async () => {
        const scriptData = parseJsonToEditorStateWithInstruction(getValues()[scriptFieldName]);
        const instruction = scriptData.instructions.find(
          (item) => item.id === instructionId && item.type === InstructionTypes.documentInstruction,
        );

        if (instruction) {
          const { data } = instruction as DocumentPageInstruction;
          setComment(instruction.comment);
          setScreenLocation(data.screenLocation);
          setDocumentId(data.documentId);
          setPage(data.page);
          setInitialState({
            comment: instruction.comment,
            screenLocation: data.screenLocation,
            documentId: data.documentId,
            page: data.page,
          });
        } else {
          setComment(defaultState.comment);
          setScreenLocation(defaultState.screenLocation);
          setDocumentId(defaultState.documentId);
          setPage(defaultState.page);
          setInitialState(defaultState);
        }
      })();
    } else {
      setComment(defaultState.comment);
      setScreenLocation(defaultState.screenLocation);
      setDocumentId(defaultState.documentId);
      setPage(defaultState.page);
      setInitialState(defaultState);
    }
  }, [
    getValues,
    instructionId,
    setComment,
    setScreenLocation,
    setDocumentId,
    setInitialState,
    setPage,
  ]);

  useEffect(() => {
    if (!documents.loading) {
      (async () => {
        const { data } = currentProject;
        if (data) {
          const params: ListParams = {
            sort_dir: 'DESC',
            page_size: 50,
            filter: {
              project_id: data.id,
              is_media: 1,
              media_type_id: 4,
            },
          };
          if (documents.data === null) {
            dispatch(getDocumentListRequest(params));
          } else {
            const projectId = getQueryFilter('project_id', documents.data.filters.active);

            if (projectId !== undefined && String(projectId.value) !== String(data.id)) {
              dispatch(getDocumentListRequest(params));
            }
          }
        }
      })();
    }
  }, [dispatch, documents, currentProject]);

  const documentList = useMemo(() => {
    if (documents.data) {
      const filteredItems = filterProcessedFiles(documents.data.items);
      const sortedDocuments = sortDocumentsByTitle(filteredItems).map((item) => ({
        id: item.document_id,
        label: item.ref.document?.title || 'Document',
      }));

      return [{ id: -1, label: 'None' }, ...sortedDocuments];
    }

    return [];
  }, [documents.data]);

  const pagesList = useMemo(() => {
    if (documentPages.data) {
      const pages = documentPages.data.map((item) => {
        const url = getSmallDocumentThumbnailUrl(item.thumbnails || []);
        return {
          id: item.id,
          label: String(item.id),
          title: <SmallImageItem text={String(item.id)} imageUrl={url} />,
        };
      });
      return [{ id: -1, label: 'None' }, ...pages];
    }

    return [];
  }, [documentPages.data]);

  const documentUrl = useMemo(() => {
    if (documentPages.data) {
      const documentPage = documentPages.data.find((item) => item.id === page);
      if (documentId > 0 && documentPage) {
        return getLargeDocumentThumbnailUrl(documentPage.thumbnails || []);
      }
    }
    return '';
  }, [documentPages.data, page, documentId]);

  const showKebabMenu = useMemo(() => {
    const scriptData = parseJsonToEditorStateWithInstruction(getValues()[scriptFieldName]);
    return scriptData.instructions.some(
      (item) => item.id === instructionId,
    ) || scriptData.blocks.some((item) => item.data.instructionId === instructionId);
  }, [getValues, instructionId]);

  const closeHandler = useCallback(() => {
    if (instanceEditor) {
      emitInstructionEventByEditor(
        instanceEditor,
        InstructionEvent.SET_DOCUMENT_PAGE_INSTRUCTION_SELECTION,
        {},
      );
    }
    dispatch(setOpenDocumentPageInstructionModal(false));
  }, [dispatch, instanceEditor]);

  const getDocumentTitle = useCallback(() => {
    if (documents.data) {
      const article = documents.data.items.find((item) => item.document_id === documentId);
      if (article) {
        return article.ref.document?.title || 'Document';
      }
    }
    return '';
  }, [documents, documentId]);

  const getSummary = useCallback(() => {
    const title = getDocumentTitle();

    if (title) {
      return `${title} - ${page}`;
    }

    if (comment) {
      return comment;
    }

    return 'Document Page';
  }, [getDocumentTitle, page, comment]);

  const saveDocumentPageInstruction = useCallback(() => {
    if (instanceEditor) {
      (async () => {
        const scriptData = parseJsonToEditorStateWithInstruction(getValues()[scriptFieldName]);
        const instructionIndex = scriptData.instructions.findIndex(
          (item) => item.id === instructionId && item.type === InstructionTypes.documentInstruction,
        );
        const documentPageInstruction: DocumentPageInstruction = {
          id: instructionId,
          type: InstructionTypes.documentInstruction,
          comment,
          needsInfo: false,
          data: {
            page,
            documentId,
            screenLocation,
          },
        };
        const summary = getSummary();
        if (instructionIndex !== -1) {
          scriptData.instructions[instructionIndex] = documentPageInstruction;
          if (isInline) {
            setInstructionActionTooltip(
              {
                instructionId,
                tooltip: `${InstructionActionTooltip.documentPage}: ${summary}`,
                scriptBlocks: scriptData.blocks,
              },
            );
            emitInstructionEventByEditor(
              instanceEditor,
              InstructionEvent.UPDATE_DOCUMENT_PAGE_ACTION_EVENT_LISTENERS,
              {
                instanceEditor,
                refEditor: window.scriptEditorRef!,
              },
            );
          }
          instanceEditor.events.emit(InstructionEvent.SUBMIT_DATA, {});
        } else {
          const id = (instructionId.trim()) ? instructionId : uuid();
          documentPageInstruction.id = id;
          scriptData.instructions.push(documentPageInstruction);
          if (isInline) {
            emitInstructionEventByEditor(
              instanceEditor,
              InstructionEvent.SAVE_DOCUMENT_PAGE_INSTRUCTION,
              { instructionId: id, tooltip: summary },
            );
          }
        }
        if (!isInline) {
          emitInstructionEventByEditor(
            instanceEditor,
            InstructionEvent.SAVE_DOCUMENT_PAGE_INSTRUCTION_BLOCK,
            { instructionId: documentPageInstruction.id, text: summary, tooltip: summary },
          );
        }
        setValue(scriptFieldName, JSON.stringify(scriptData));
        closeHandler();
      })();
    }
  }, [
    instanceEditor,
    getValues,
    setValue,
    documentId,
    page,
    comment,
    screenLocation,
    instructionId,
    closeHandler,
    isInline,
    getSummary,
  ]);

  const cancelInstruction = useCallback(() => {
    (async () => {
      setComment(initialState.comment);
      setScreenLocation(initialState.screenLocation);
      setDocumentId(initialState.documentId);
      setPage(initialState.page);
      const scriptData = parseJsonToEditorStateWithInstruction(getValues()[scriptFieldName]);
      const isInstructionCreated = scriptData.instructions.some(
        (item) => item.id === instructionId && item.type === InstructionTypes.documentInstruction,
      ) || scriptData.blocks.some((item) => item.data.instructionId === instructionId);
      if (instanceEditor) {
        if (!isInstructionCreated) {
          emitInstructionEventByEditor(
            instanceEditor,
            InstructionEvent.REMOVE_INSTRUCTION_BLOCK_WITHOUT_SAVING,
            {},
          );
        }
        emitInstructionEventByEditor(
          instanceEditor,
          InstructionEvent.CANCEL_INSTRUCTION,
          {},
        );
      }
      closeHandler();
    })();
  }, [
    setComment,
    setScreenLocation,
    setDocumentId,
    setPage,
    initialState,
    closeHandler,
    instanceEditor,
    instructionId,
    getValues,
  ]);

  const removeHandler = useCallback(() => {
    if (!isInline && instanceEditor) {
      emitInstructionEventByEditor(
        instanceEditor,
        InstructionEvent.REMOVE_INSTRUCTION_BLOCK,
        {},
      );
    } else {
      removeInstructionElement({ instructionId, isInline })
        .then(() => {
          if (instanceEditor) {
            instanceEditor.events.emit(InstructionEvent.SUBMIT_DATA, {});
          }
        });
    }
    closeHandler();
  }, [closeHandler, instructionId, isInline, instanceEditor]);

  const onDocumentChangeHandler = useCallback((option?: Option) => {
    if (option) {
      const optionId = parseInt(option.id.toString(), 10);

      setDocumentId(optionId);
      if (optionId > 0) {
        dispatch(getArticleDocumentPagesRequest(optionId));
      }
    }
  }, [dispatch, setDocumentId]);

  const onPageChangeHandler = useCallback((option?: Option) => {
    if (option) {
      setPage(parseInt(option.id.toString(), 10));
    }
  }, []);
  const openPreview = useCallback(() => {
    if (documentId !== -1) {
      dispatch(setMediaList([{
        url: '',
        title: getDocumentTitle(),
        id: documentId,
        typeId: 4,
      }]));
      const indexPage = page === 0 ? 0 : page - 1;
      dispatch(setCurrentDocumentPageMediaViewer(indexPage));
      dispatch(setOpenMediaViewer(true));
      addAction((pageNumber) => { setPage(pageNumber); });
    }
  }, [dispatch, documentId, page, getDocumentTitle, setPage, addAction]);

  const duplicate = useCallback(() => {
    const scriptData = parseJsonToEditorStateWithInstruction(getValues().script);
    const notifHandler = () => dispatch(
      notify({
        type: 'success',
        icon: 'copy',
        message: 'Instruction duplicated.',
      }),
    );

    if (instanceEditor) {
      const duplicateBlockService = new DuplicateBlock(
        instanceEditor, notifHandler,
        setValue,
        instructionId,
        scriptData,
      );

      if (!isInline) {
        duplicateBlockService.duplicateBlock();
      } else {
        duplicateBlockService.duplicateInline();
      }
    }
  }, [instanceEditor, getValues, instructionId, isInline, setValue, dispatch]);

  useEffect(() => {
    if (documents.data) {
      if (documentId > -1 && !documentPages.data && !documentPages.loading) {
        dispatch(getArticleDocumentPagesRequest(documentId));
      }
    }
  }, [dispatch, documentId, documentPages, documents]);

  return (
    <ModalComponent
      open={isOpenModal}
      title={getSummary()}
      saveHandler={saveDocumentPageInstruction}
      cancelHandler={cancelInstruction}
      deleteHandler={removeHandler}
      duplicate={duplicate}
      showKebabMenu={showKebabMenu}
    >
      <Grid container spacing={3} justify="space-between">
        <Grid item xs={12}>
          <InputComponent
            type="textarea"
            label="Comment"
            value={comment}
            onChange={setComment}
            autoFocus
          />
        </Grid>
        <Grid item container spacing={3} xs={6} alignContent="flex-start">
          <Grid item xs={12} className={classes.selectImageWrapper}>
            <FormSimpleAutoComplete
              name="document"
              label="Document"
              options={documentList}
              callback={onDocumentChangeHandler}
              isClearValueIfOptionNull
            />
          </Grid>
          <Grid item xs={12} className={classes.selectImageWrapper}>
            <FormSimpleAutoComplete
              name="page"
              label="Page"
              options={pagesList}
              callback={onPageChangeHandler}
              disabled={documentId < 0}
            />
          </Grid>
          <Grid item xs={12}>
            <SelectComponent
              label="Screen Location"
              value={screenLocation}
              items={ScreenLocations}
              changeHandler={setScreenLocation}
              isNone={false}
            />
          </Grid>
        </Grid>
        <Grid item container spacing={3} xs={6} alignItems="flex-start" alignContent="flex-start">
          <p className={classes.imageTitle}>Preview</p>
          <Grid item container xs={12} alignItems="center" justify="center" className={classes.previewContainer}>
            {
              documentId < 0
                ? <MediaFileMissingMessage />
                : (
                  <>
                    <img onClick={openPreview} src={documentUrl} alt="" className={classes.image} />
                    {(!documentUrl && documentId !== -1) && <ProcessingMedia />}
                  </>
                )
            }
          </Grid>
        </Grid>
      </Grid>
    </ModalComponent>
  );
};

export default DocumentInstructionModal;
